<!--
  @component DomainCreateWizard — Phase 3 "Add domain" modal.
  Register a domain you already own (BYO), then optionally route it to a
  project (its app serves there once DNS points at it).
  reads:  useDeploymentIO (domain.register + domain.attach), useIdentityIO (projects)
  steps:  Domain → Route → Review
-->
<script lang="ts">
  import Wizard, { type WizardStepDef } from "../../../../global/visuals/primitives/Wizard.svelte";
  import { useIdentityIO } from "../../accounts/IdentityIO.svelte";
  import { useDeploymentIO } from "../../../../global/io/nodes/deployment/DeploymentIO.svelte";

  interface Props { open: boolean; onClose: () => void; onCreated?: () => void; }
  const { open, onClose, onCreated }: Props = $props();

  const identity = useIdentityIO();
  const node = useDeploymentIO();

  const STEPS: readonly WizardStepDef[] = [
    { id: "domain", label: "Domain" },
    { id: "route", label: "Route" },
    { id: "review", label: "Review" },
  ];

  let step = $state(0);
  let domain = $state("");
  let routeTo = $state<string>(""); // "" = don't route yet
  let pending = $state(false);
  let error = $state<string | null>(null);
  let phase = $state<string>("");

  const projects = $derived(identity.projects);

  $effect(() => { if (open) { step = 0; domain = ""; routeTo = ""; pending = false; error = null; phase = ""; } });

  const DOMAIN_RE = /^(?=.{1,253}$)(?!-)[a-z0-9-]{1,63}(?<!-)(\.[a-z0-9-]{1,63})+$/;
  const trimmed = $derived(domain.trim().toLowerCase());
  const domainError = $derived.by<string | null>(() => {
    if (!trimmed) return null;
    if (!DOMAIN_RE.test(trimmed)) return "enter a valid domain, e.g. acme.com";
    return null;
  });

  function nameOf(id: string): string { return projects.find((p) => p.id === id)?.name ?? id; }

  const canAdvance = $derived.by(() => {
    if (pending) return false;
    if (step === 0) return trimmed.length > 0 && domainError === null;
    return true;
  });

  async function finish(): Promise<void> {
    if (!canAdvance || pending || !trimmed) return;
    pending = true; error = null;
    try {
      phase = "Registering domain…";
      await node.dispatch("domain.register", { domain: trimmed });
      if (routeTo) {
        phase = `Routing to ${nameOf(routeTo)}…`;
        await node.dispatch("domain.attach", { target: { id: routeTo }, domain: trimmed });
      }
      onCreated?.();
      onClose();
    } catch (e) {
      error = e instanceof Error ? e.message : "could not add domain";
    } finally {
      pending = false; phase = "";
    }
  }
</script>

<Wizard
  {open}
  title="Add domain"
  steps={STEPS}
  currentStep={step}
  {canAdvance}
  {pending}
  finishLabel="Add domain"
  onStepChange={(n) => (step = n)}
  onFinish={() => void finish()}
  onCancel={onClose}
>
  {#snippet children(stepIndex: number)}
    {#if stepIndex === 0}
      <div class="step">
        <label>
          <span>domain</span>
          <input type="text" placeholder="acme.com" autocomplete="off" spellcheck="false" bind:value={domain} disabled={pending} />
        </label>
        <p class="hint" class:bad={!!domainError}>
          {domainError ?? "A domain you already own (BYO). Point its DNS at your app to serve it — set up in the project's Domain tab."}
        </p>
      </div>
    {:else if stepIndex === 1}
      <div class="step">
        <p class="lbl">Route to project</p>
        <p class="hint">Which app should this domain serve? You can leave it unrouted and assign it later in the matrix. A domain routes to one app at a time.</p>
        <div class="list">
          <button type="button" class="opt" class:sel={routeTo === ""} disabled={pending} onclick={() => (routeTo = "")}>
            <span class="radio" class:on={routeTo === ""}></span>
            <span class="opt-name muted">Don't route yet</span>
          </button>
          {#each projects as p (p.id)}
            <button type="button" class="opt" class:sel={routeTo === p.id} disabled={pending} onclick={() => (routeTo = p.id)}>
              <span class="radio" class:on={routeTo === p.id}></span>
              <span class="opt-name">{p.name}</span>
            </button>
          {/each}
        </div>
      </div>
    {:else}
      <div class="step review">
        <dl>
          <div class="row"><dt>domain</dt><dd><code>{trimmed}</code></dd></div>
          <div class="row"><dt>source</dt><dd>BYO (you own it)</dd></div>
          <div class="row"><dt>routes to</dt><dd>{routeTo ? nameOf(routeTo) : "— (unrouted)"}</dd></div>
        </dl>
        <p class="note"><strong>Next:</strong> we register the domain to your account{routeTo ? " and route it to the selected app" : ""}. The app serves it once the domain's DNS points at it (finish DNS in the project's Domain tab).</p>
        {#if pending && phase}<p class="phase">{phase}</p>{/if}
        {#if error}<p class="error">{error}</p>{/if}
      </div>
    {/if}
  {/snippet}
</Wizard>

<style>
  .step { display: flex; flex-direction: column; gap: var(--space-2); }
  label { display: flex; flex-direction: column; gap: var(--space-1, 4px); }
  label span { font-size: 11px; text-transform: uppercase; letter-spacing: 0.08em; color: var(--text-tertiary); }
  input { padding: var(--space-2) var(--space-3); border-radius: var(--radius-sm); border: 0.5px solid var(--border-input); background: var(--surface-sunken); color: var(--text-primary); font: inherit; font-size: 13px; font-family: var(--type-mono); }
  input:focus { outline: none; border-color: var(--border-focus); }
  .lbl { margin: 0; font-size: 11px; text-transform: uppercase; letter-spacing: 0.08em; color: var(--text-tertiary); }
  .hint { margin: 0 0 var(--space-1); font-size: 11.5px; color: var(--text-tertiary); line-height: 1.5; }
  .hint.bad { color: var(--error); font-family: var(--type-mono); }
  .list { display: flex; flex-direction: column; gap: 2px; max-height: 280px; overflow-y: auto; scrollbar-width: thin; }
  .opt { display: flex; align-items: center; gap: var(--space-2); background: transparent; border: 0.5px solid transparent; border-radius: var(--radius-md); padding: 9px var(--space-2); cursor: pointer; text-align: left; font: inherit; width: 100%; transition: background var(--ease-fast), border-color var(--ease-fast); }
  .opt:hover:not(:disabled) { background: var(--bg-hover); }
  .opt.sel { background: var(--bg-selected); border-color: var(--border-focus); }
  .opt:disabled { cursor: not-allowed; opacity: 0.6; }
  .opt-name { flex: 1; min-width: 0; font-size: 13.5px; color: var(--text-primary); overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
  .opt-name.muted { color: var(--text-tertiary); }
  .radio { width: 15px; height: 15px; flex-shrink: 0; border: 1px solid var(--border-strong); background: var(--surface-sunken); border-radius: var(--radius-pill); }
  .radio.on { border-color: var(--accent, #5b8cff); box-shadow: inset 0 0 0 3px var(--accent, #5b8cff); }
  .review dl { display: flex; flex-direction: column; gap: var(--space-2); margin: 0; }
  .row { display: flex; gap: var(--space-3); align-items: baseline; }
  dt { flex: 0 0 110px; font-size: 11px; text-transform: uppercase; letter-spacing: 0.08em; color: var(--text-tertiary); }
  dd { margin: 0; font-size: 12.5px; color: var(--text-primary); }
  dd code { font-family: var(--type-mono); font-size: 12px; }
  .note { margin: var(--space-2) 0 0; padding: var(--space-3); background: var(--warning-subtle); border-radius: var(--radius-sm); font-size: 12px; color: var(--text-secondary); line-height: 1.55; }
  .note strong { color: var(--warning); font-weight: 500; }
  .phase { margin: var(--space-2) 0 0; font-size: 12.5px; color: var(--text-secondary); }
  .error { margin: var(--space-2) 0 0; font-size: 12px; color: var(--error); font-family: var(--type-mono); }
</style>
